<script>
  import { page } from '$app/state';
  import { strip_invisible_characters, to_plain_text } from '#lib/util.js';

  let {
    /** @type {string} */
    title = '',
    /** @type {string} */
    description = '',
    /** @type {string} */
    url = '',
    /** @type {string} */
    image = '',
    /** @type {'website' | 'article' | 'product' | 'book' | 'profile' | 'video' | 'event'} */
    type = 'website',
    /** @type {string} */
    author = '',
    /** @type {Record<string, any>} */
    json_ld = {},
    /** @type {boolean} keep search engines from indexing this page */
    noindex = false
  } = $props();

  // The address without its query: tracking tags (utm_*, fbclid, gclid) and UI state (?edit, ?from)
  // would otherwise make every variant claim to be the original. Pages whose query is content pass `url`.
  let _url = $derived(url || (page.url ? `${page.url.origin}${page.url.pathname}` : ''));

  // matching our entry types
  const jspn_ld_context_map = {
    website: 'https://schema.org',
    article: 'https://schema.org/Article',
    product: 'https://schema.org/Product',
    book: 'https://schema.org/Book',
    profile: 'https://schema.org/Person',
    video: 'https://schema.org/VideoObject',
    event: 'https://schema.org/Event',
  };

  /**
   * Every consumer of these tags reads plain text, so the authored string is stripped down to it:
   * markdown annotations come off, entities are decoded (authors write `&shy;` to hyphenate body
   * copy, and it reaches the head as seven literal characters otherwise) and the invisible break
   * hints go with them, since nothing in a tab title or a search snippet wraps.
   */
  function to_meta_text(value) {
    return strip_invisible_characters(to_plain_text(value));
  }

  let title_no_markdown = $derived(to_meta_text(title));
  let description_no_markdown = $derived(to_meta_text(description));

  function generate_json_ld() {
    const json = {
      '@context': jspn_ld_context_map[type] || 'https://schema.org',
      '@type': type || 'website',
      name: title_no_markdown,
      description: description_no_markdown,
      url: _url || '',
      image: image || '',
      ...json_ld
    };
    // The JSON is written into a <script> element, where a `<` in any value would end it early.
    return JSON.stringify(json).replace(/</g, '\\u003c');
  }

  let json_ld_string = $derived(generate_json_ld());
</script>

<svelte:head>
  {#if title_no_markdown?.length > 0}
    <title>{title_no_markdown}</title>
    <meta property="og:title" content={title_no_markdown}>
    <meta name="twitter:title" content={title_no_markdown}>
  {/if}

  <meta property="og:type" content={type}>

  {#if description_no_markdown?.length > 0}
    <meta name="description" content={description_no_markdown}>
    <meta property="og:description" content={description_no_markdown}>
    <meta name="twitter:description" content={description_no_markdown}>
  {/if}

  {#if _url}
    <link rel="canonical" href={_url}>
    <meta property="og:url" content={_url}>
    <meta name="twitter:url" content={_url}>
  {/if}
    
  {#if image}
    <meta name="twitter:card" content="summary_large_image">
    <meta property="og:image" content={image}>
    <meta name="twitter:image" content={image}>
  {/if}

  {#if author}
    <meta name="author" content={author}>
  {/if}

  {#if noindex}
    <meta name="robots" content="noindex">
  {/if}
  
  {@html '<script type="application/ld+json">' + json_ld_string + '</script>'}
</svelte:head>